'use client';

import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SUBSCRIPTION } from '../constants/routes';

type BlockReason = 'locked' | 'expired' | 'trial_expired' | 'unknown';

function getStateValue(state: any, key: string): any {
  if (!state || typeof state !== 'object') return undefined;
  return (state as any)[key];
}

const LockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="11" width="14" height="10" rx="2" stroke="#ef4444" strokeWidth="1.5" fill="#FEE2E2" />
    <path d="M8 11V7a4 4 0 018 0v4" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1.5" fill="#ef4444" />
  </svg>
);

const ClockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#f59e0b" strokeWidth="1.5" fill="#FEF3C7" />
    <path d="M12 7v5l3 3" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TrialClockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.5" fill="#FEE2E2" />
    <path d="M12 7v5l3 3" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AlertIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#7c3aed" strokeWidth="1.5" fill="#EDE9FE" />
    <path d="M12 8v5" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="15.5" r="1" fill="#7c3aed" />
  </svg>
);

const reasonConfig: Record<BlockReason, {
  tag: string;
  tagClass: string;
  iconBgClass: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  btnText: string;
  btnClass: string;
}> = {
  locked: {
    tag: 'Access Locked',
    tagClass: 'bg-red-100 text-red-700',
    iconBgClass: 'bg-red-50',
    icon: <LockIcon />,
    title: 'Sport Locked',
    description: 'Your plan for this sport is locked. Please upgrade to continue.',
    btnText: 'Upgrade Plan',
    btnClass: 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
  },
  expired: {
    tag: 'Plan Expired',
    tagClass: 'bg-amber-100 text-amber-700',
    iconBgClass: 'bg-amber-50',
    icon: <ClockIcon />,
    title: 'Plan Expired',
    description: 'Your plan for this sport has expired. Please renew to continue.',
    btnText: 'Renew Plan',
    btnClass: 'from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
  },
  trial_expired: {
    tag: 'Trial Expired',
    tagClass: 'bg-red-100 text-red-700',
    iconBgClass: 'bg-red-50',
    icon: <TrialClockIcon />,
    title: 'Trial Expired',
    description: 'Your trial for this sport has ended. Please upgrade to continue.',
    btnText: 'Upgrade Plan',
    btnClass: 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
  },
  unknown: {
    tag: 'Unavailable',
    tagClass: 'bg-violet-100 text-violet-700',
    iconBgClass: 'bg-violet-50',
    icon: <AlertIcon />,
    title: 'Access Unavailable',
    description: 'Your plan for this sport is unavailable. Please upgrade to continue.',
    btnText: 'Upgrade Plan',
    btnClass: 'from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700',
  },
};

export default function SportAccess() {
  const location = useLocation();
  const navigate = useNavigate();

  const sportCode = useMemo(() => String(getStateValue(location.state, 'sportCode') || ''), [location.state]);
  const reason = useMemo(
    () => (String(getStateValue(location.state, 'reason') || '') as BlockReason) || 'unknown',
    [location.state],
  );

  const config = reasonConfig[reason] ?? reasonConfig.unknown;

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#E8EBF3] via-[#F0F3FA] to-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">

        {/* Icon */}
        <div className={`w-[72px] h-[72px] rounded-full ${config.iconBgClass} flex items-center justify-center mx-auto mb-4`}>
          {config.icon}
        </div>

        {/* Tag */}
        <span className={`inline-block text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mb-3 ${config.tagClass}`}>
          {config.tag}
        </span>

        {/* Title & Description */}
        <h1 className="text-3xl font-bold text-slate-900 mb-2">{config.title}</h1>
        {sportCode && <p className="text-xs font-semibold text-slate-400 mb-2">Sport: {sportCode}</p>}
        <p className="text-slate-500 text-sm leading-relaxed mb-6">{config.description}</p>

        {/* Divider */}
        <div className="border-t border-slate-100 mb-6" />

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            type="button"
              onClick={() => navigate(SUBSCRIPTION)}
              className={`mx-auto w-[200px] px-6 py-3 rounded-lg bg-gradient-to-r ${config.btnClass} text-white font-semibold transition-all shadow-md`}
          >
            {config.btnText}
          </button>
        </div>

      </div>
    </div>
  );
}
